/* eslint-disable max-lines */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  Button,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  Chip,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Check as CheckIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Cancel as CancelIcon,
  Security as SecurityIcon,
  Undo as UndoIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon
} from '@mui/icons-material';
import CSVUploader from './CSVUploader';
import FieldMapper from './FieldMapper';
import DuplicateResolver from './DuplicateResolver';
import ImportProgress from './ImportProgress';
import CSVImportInstructions from './CSVImportInstructions';
import RollbackConfirmationDialog from './RollbackConfirmationDialog';
import { detectFieldMappings } from '../../utils/fieldDetection';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const steps = [
  'Upload CSV File',
  'Map Fields',
  'Review Duplicates',
  'Import Data',
  'View Results'
];

// Helper function to set nested values in objects
const setNestedValue = (obj, path, value) => {
  const keys = path.split('.');
  let current = obj;
  for (let i = 0; i < keys.length - 1; i++) {
    if (!current[keys[i]]) {
      current[keys[i]] = {};
    }
    current = current[keys[i]];
  }
  current[keys[keys.length - 1]] = value;
};

// Helper function to chunk large arrays for processing
const chunkArray = (array, chunkSize) => {
  const chunks = [];
  for (let i = 0; i < array.length; i += chunkSize) {
    chunks.push(array.slice(i, i + chunkSize));
  }
  return chunks;
};

// eslint-disable-next-line max-lines-per-function
function CSVImportWizard({ onImportComplete }) {
  const { user, isPlatformAdmin } = useAuth();
  const [activeStep, setActiveStep] = useState(0);
  const [csvData, setCsvData] = useState(null);
  const [mappings, setMappings] = useState({});
  const [duplicates, setDuplicates] = useState([]);
  const [duplicateResolutions, setDuplicateResolutions] = useState({});
  const [importProgress, setImportProgress] = useState({
    total: 0,
    processed: 0,
    successful: 0,
    failed: 0,
    status: 'idle',
    errors: [],
    importId: null
  });
  const [importResults, setImportResults] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [csvSize, setCsvSize] = useState(0);
  const [showSizeWarning, setShowSizeWarning] = useState(false);

  // Rollback state management
  const [isRollbackDialogOpen, setIsRollbackDialogOpen] = useState(false);
  const [rollbackProgress, setRollbackProgress] = useState(null);
  const [rollbackError, setRollbackError] = useState(null);
  const [showRollbackStatus, setShowRollbackStatus] = useState(false);

  // Check platform admin status from JWT claims - no API call needed
  useEffect(() => {
    if (!user) {
      setError('You must be logged in to use bulk import.');
      return;
    }

    // Platform admin status comes directly from JWT custom claims via AuthContext
    // No API call needed - this is already available from the token
    if (!isPlatformAdmin) {
      setError('Only platform administrators can perform bulk imports. Please contact your system administrator for access.');
    }
  }, [user, isPlatformAdmin]);

  // Handle CSV upload
  const handleCsvUpload = (data, fileSize) => {
    // Check platform admin status first
    if (!isPlatformAdmin) {
      setError('Only platform administrators can perform bulk imports. Please contact your system administrator for access.');
      return;
    }

    // Memory management - check file size
    const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
    const WARN_FILE_SIZE = 1 * 1024 * 1024; // 1MB

    setCsvSize(fileSize);

    if (fileSize > MAX_FILE_SIZE) {
      setError(`File too large (${(fileSize / 1024 / 1024).toFixed(1)}MB). Maximum allowed size is 10MB. Please split your file into smaller chunks.`);
      return;
    }

    if (fileSize > WARN_FILE_SIZE) {
      setShowSizeWarning(true);
    } else {
      setShowSizeWarning(false);
    }

    setCsvData(data);
    setError(null);

    // Auto-detect field mappings
    if (data && data.headers) {
      const detectedMappings = detectFieldMappings(data.headers);
      setMappings(detectedMappings);
    }

    // Automatically move to mapping step
    setActiveStep(1);
  };

  // Handle field mapping changes
  const handleMappingChange = (newMappings) => {
    setMappings(newMappings);
  };

  // Check for duplicates
  const checkDuplicates = async () => {
    // Check platform admin status first
    if (!isPlatformAdmin) {
      setError('Only platform administrators can perform bulk imports. Please contact your system administrator for access.');
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      // Prepare data for duplicate check - transform to nested structure
      const mappedData = csvData.rows.map(row => {
        const entity = { type: 'PERSON' };
        Object.keys(mappings).forEach(csvField => {
          const entityField = mappings[csvField];
          if (entityField && row[csvField]) {
            if (entityField.includes('.')) {
              // Handle nested field (e.g., 'faana.leadership_quality_scores.courage_score')
              setNestedValue(entity, entityField, row[csvField]);
            } else {
              // Handle flat field
              entity[entityField] = row[csvField];
            }
          }
        });
        return entity;
      });

      // For large datasets, process in chunks to avoid memory issues
      const CHUNK_SIZE = 500; // Process 500 rows at a time
      let allDuplicates = [];

      if (mappedData.length > CHUNK_SIZE) {
        const chunks = chunkArray(mappedData, CHUNK_SIZE);
        for (let i = 0; i < chunks.length; i++) {
          const chunkResponse = await api.checkEntityDuplicates(chunks[i]);
          const chunkDuplicates = chunkResponse.duplicates || [];

          // Adjust row indices for chunk offset
          const adjustedDuplicates = chunkDuplicates.map(dup => ({
            ...dup,
            rowIndex: dup.rowIndex + (i * CHUNK_SIZE)
          }));

          allDuplicates = allDuplicates.concat(adjustedDuplicates);
        }
      } else {
        // Call API to check for duplicates with correct format
        const response = await api.checkEntityDuplicates(mappedData);
        allDuplicates = response.duplicates || [];
      }

      setDuplicates(allDuplicates);

      // Initialize resolutions with default actions
      const defaultResolutions = {};
      allDuplicates.forEach(dup => {
        defaultResolutions[dup.rowIndex] = 'create_new';
      });
      setDuplicateResolutions(defaultResolutions);

      setActiveStep(2);
    } catch (err) {
      console.error('Error checking duplicates:', err);
      setError('Failed to check for duplicates. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle duplicate resolution changes
  const handleDuplicateResolution = (rowIndex, action, targetEntityId = null) => {
    setDuplicateResolutions(prev => ({
      ...prev,
      [rowIndex]: { action, targetEntityId }
    }));
  };

  // Start the import process
  const startImport = async () => {
    // Check platform admin status first
    if (!isPlatformAdmin) {
      setError('Only platform administrators can perform bulk imports. Please contact your system administrator for access.');
      return;
    }

    setIsProcessing(true);
    setError(null);
    setActiveStep(3);

    try {
      // Prepare import data - transform to nested structure
      const entities = csvData.rows.map((row, index) => {
        const entity = { type: 'PERSON' };
        Object.keys(mappings).forEach(csvField => {
          const entityField = mappings[csvField];
          if (entityField && row[csvField]) {
            if (entityField.includes('.')) {
              // Handle nested field (e.g., 'faana.leadership_quality_scores.courage_score')
              setNestedValue(entity, entityField, row[csvField]);
            } else {
              // Handle flat field
              entity[entityField] = row[csvField];
            }
          }
        });

        // Add duplicate resolution if applicable
        if (duplicateResolutions[index]) {
          entity._duplicateResolution = duplicateResolutions[index];
        }

        return entity;
      });

      const importRequest = {
        data: entities, // Backend expects 'data' not 'entities'
        mappings,
        importType: 'person_bulk',
        userId: user.uid,
        userEmail: user.email
      };

      // Start the import
      const response = await api.bulkImportEntities(importRequest);

      if (response.importId) {
        setImportProgress(prev => ({
          ...prev,
          importId: response.importId,
          total: entities.length,
          status: 'processing'
        }));

        // Poll for progress
        pollImportProgress(response.importId);
      }
    } catch (err) {
      console.error('Error starting import:', err);
      setError('Failed to start import. Please try again.');
      setIsProcessing(false);
    }
  };

  // Poll for import progress
  const pollImportProgress = async (importId) => {
    let pollAttempts = 0;
    const maxPollAttempts = 150; // Maximum 5 minutes (150 * 2s)
    const pollInterval = setInterval(async () => {
      pollAttempts++;

      try {
        const progress = await api.getImportProgress(importId);

        // Ensure we have the correct structure
        const normalizedProgress = {
          importId,
          status: progress.status || 'processing',
          total: progress.total || 0,
          processed: progress.processed || 0,
          successful: progress.successful || 0,
          failed: progress.failed || 0,
          errors: progress.errors || [],
          results: progress.results || null
        };

        setImportProgress(normalizedProgress);

        // Check for completion or failure
        if (normalizedProgress.status === 'completed' || normalizedProgress.status === 'failed' || normalizedProgress.status === 'cancelled' || normalizedProgress.status === 'rolling_back') {
          clearInterval(pollInterval);
          setIsProcessing(false);

          if (normalizedProgress.status === 'completed') {
            const results = normalizedProgress.results || {
              successful: normalizedProgress.successful,
              failed: normalizedProgress.failed,
              updated: 0,
              skipped: 0,
              errors: normalizedProgress.errors,
              canRollback: normalizedProgress.canRollback || (normalizedProgress.successful > 0),
              importId: normalizedProgress.importId
            };
            setImportResults(results);
            setActiveStep(4);

            // Notify parent component
            // eslint-disable-next-line max-depth
            if (onImportComplete) {
              onImportComplete(results);
            }
          } else if (normalizedProgress.status === 'failed') {
            setError(`Import failed: ${normalizedProgress.errorMessage || 'Please check the error details and try again.'}`);
          } else if (normalizedProgress.status === 'cancelled') {
            setError('Import was cancelled.');
          } else if (normalizedProgress.status === 'rolling_back') {
            // Update rollback progress
            setRollbackProgress({
              status: 'rolling_back',
              deletedCount: normalizedProgress.processed || 0,
              totalToDelete: normalizedProgress.total || 0,
              failedDeletes: normalizedProgress.errors || []
            });
          }
        }

        // Reset poll attempts on successful response
        pollAttempts = 0;

      } catch (err) {
        console.error('Error polling progress:', err);

        // Only fail after multiple consecutive failures
        if (pollAttempts >= 5) {
          clearInterval(pollInterval);
          setIsProcessing(false);
          setError('Lost connection to import service. The import may still be running. Please refresh the page to check status.');
        }
      }

      // Timeout protection
      if (pollAttempts >= maxPollAttempts) {
        clearInterval(pollInterval);
        setIsProcessing(false);
        setError('Import is taking longer than expected. Please check the status later or contact support.');
      }
    }, 2000); // Poll every 2 seconds
  };

  // Cancel import
  const cancelImport = async () => {
    if (importProgress.importId && window.confirm('Are you sure you want to cancel this import?')) {
      try {
        const response = await api.cancelImport(importProgress.importId);
        if (response.success) {
          setImportProgress(prev => ({ ...prev, status: 'cancelled' }));
          setIsProcessing(false);
        } else {
          setError(`Failed to cancel import: ${response.message || 'Unknown error'}`);
        }
      } catch (err) {
        console.error('Error cancelling import:', err);
        setError('Failed to cancel import. It may have already completed or failed.');
      }
    }
  };

  // Navigate steps
  const handleNext = () => {
    if (activeStep === 1) {
      // Moving from mapping to duplicate check
      checkDuplicates();
    } else if (activeStep === 2) {
      // Moving from duplicates to import
      startImport();
    } else {
      setActiveStep((prevStep) => prevStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleReset = () => {
    setActiveStep(0);
    setCsvData(null);
    setMappings({});
    setDuplicates([]);
    setDuplicateResolutions({});
    setImportProgress({
      total: 0,
      processed: 0,
      successful: 0,
      failed: 0,
      status: 'idle',
      errors: [],
      importId: null
    });
    setImportResults(null);
    setIsProcessing(false);
    setError(null);
    setCsvSize(0);
    setShowSizeWarning(false);
    setIsRollbackDialogOpen(false);
    setRollbackProgress(null);
    setRollbackError(null);
    setShowRollbackStatus(false);
  };

  // Rollback import functionality
  const handleRollbackImport = async () => {
    if (!importResults?.importId) {
      setRollbackError('Import ID not found. Cannot rollback this import.');
      return;
    }

    try {
      setRollbackError(null);
      setIsRollbackDialogOpen(false);

      // Initialize rollback progress
      setRollbackProgress({
        status: 'rolling_back',
        deletedCount: 0,
        totalToDelete: importResults.successful,
        failedDeletes: []
      });

      // Start rollback
      const response = await api.rollbackImport(importResults.importId);

      if (response.success) {
        // Poll for rollback completion
        pollRollbackProgress(importResults.importId);
      } else {
        setRollbackError(`Rollback failed: ${response.message || 'Unknown error'}`);
        setRollbackProgress(null);
      }
    } catch (err) {
      console.error('Error initiating rollback:', err);
      setRollbackError(`Failed to start rollback: ${err.message || 'Unknown error'}`);
      setRollbackProgress(null);
    }
  };

  // Poll rollback progress (reuse import progress polling with rollback context)
  const pollRollbackProgress = async (importId) => {
    let pollAttempts = 0;
    const maxPollAttempts = 60; // Maximum 2 minutes for rollback
    const pollInterval = setInterval(async () => {
      pollAttempts++;

      try {
        const progress = await api.getImportProgress(importId);

        if (progress.status === 'rolling_back') {
          setRollbackProgress({
            status: 'rolling_back',
            deletedCount: progress.processed || 0,
            totalToDelete: progress.total || importResults.successful,
            failedDeletes: progress.errors || []
          });
        } else if (progress.status === 'completed') {
          // Rollback completed successfully
          clearInterval(pollInterval);
          setRollbackProgress({
            status: 'completed',
            deletedCount: importResults.successful,
            totalToDelete: importResults.successful,
            failedDeletes: []
          });

          // Update import results to reflect rollback
          setImportResults(prev => ({
            ...prev,
            canRollback: false,
            rolledBack: true
          }));

          setShowRollbackStatus(true);
        } else if (progress.status === 'failed') {
          clearInterval(pollInterval);
          setRollbackError('Rollback failed. Some entities may not have been deleted.');
          setRollbackProgress(null);
        }

        pollAttempts = 0; // Reset on successful response

      } catch (err) {
        console.error('Error polling rollback progress:', err);

        if (pollAttempts >= 3) {
          clearInterval(pollInterval);
          setRollbackError('Lost connection during rollback. Please check import status.');
          setRollbackProgress(null);
        }
      }

      if (pollAttempts >= maxPollAttempts) {
        clearInterval(pollInterval);
        setRollbackError('Rollback is taking longer than expected. Please check status later.');
      }
    }, 2000); // Poll every 2 seconds
  };

  // Download template
  const downloadTemplate = () => {
    const template = `firstName,lastName,email,phone,organization,role,linkedinUrl,bio,skills,courageScore,vulnerabilityScore,purposeDrivenScore,learningAgilityScore,determinationScore,selfAwarenessScore,adaptabilityScore,crossCulturalScore,holisticHealthScore,financialFitnessScore,communityEngagementScore,leadershipStrengthScore,growthTrajectory,growthVelocity,assessmentDate,coachName,growthRiskIndex,riskFactors,notes
John,Doe,john.doe@example.com,555-0100,Acme Corp,CEO,https://linkedin.com/in/johndoe,"Experienced leader with 20 years in tech","Strategic Planning, Team Building, Innovation",2.8,2.5,2.9,2.7,2.8,2.6,2.9,2.4,85,78,92,88,Advanced,Rapid,2024-01-15,Sarah Johnson,2.3,"market_competition, talent_retention","Key contact for Q4 initiative"
Jane,Smith,jane.smith@example.com,555-0101,Tech Innovations,CTO,https://linkedin.com/in/janesmith,"Technical visionary and team builder","Cloud Architecture, AI/ML, DevOps",2.9,2.8,3.0,2.9,2.7,2.8,2.9,2.7,90,85,95,92,Advanced,Rapid,2024-02-01,Michael Torres,2.0,"skill_gaps","Potential speaker for annual conference"`;

    const blob = new Blob([template], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'person_import_template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Show access denied message for non-admin users
  if (!isPlatformAdmin) {
    return (
      <Box>
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5" component="h2">
              Bulk Import People
            </Typography>
            <SecurityIcon color="action" />
          </Box>

          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Administrative Access Required
            </Typography>
            <Typography variant="body2">
              Bulk import functionality is restricted to platform administrators only.
              This helps ensure data integrity and system security.
            </Typography>
            <Typography variant="body2" sx={{ mt: 2 }}>
              If you need to perform bulk imports, please contact your system administrator
              to request the appropriate permissions.
            </Typography>
          </Alert>

          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Button
              variant="outlined"
              startIcon={<SecurityIcon />}
              onClick={() => window.location.reload()}
            >
              Refresh Authorization
            </Button>
          </Box>
        </Paper>
      </Box>
    );
  }

  return (
    <Box>
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" component="h2">
            Bulk Import People
          </Typography>
          <Chip
            size="small"
            icon={<SecurityIcon />}
            label="Admin Access"
            color="primary"
            variant="outlined"
          />
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {showSizeWarning && (
          <Alert severity="warning" sx={{ mb: 3 }} onClose={() => setShowSizeWarning(false)}>
            Large file detected ({(csvSize / 1024 / 1024).toFixed(1)}MB). Processing may take longer than usual.
          </Alert>
        )}

        <CSVImportInstructions onDownloadTemplate={downloadTemplate} />

        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label, index) => {
            const stepProps = {};
            const labelProps = {};

            if (index === 3 && importProgress.status === 'processing') {
              labelProps.optional = (
                <Typography variant="caption">
                  Processing {importProgress.processed}/{importProgress.total}
                </Typography>
              );
            }

            if (index === 4 && rollbackProgress) {
              labelProps.optional = (
                <Typography variant="caption" color="error">
                  Rolling back... {rollbackProgress.deletedCount}/{rollbackProgress.totalToDelete}
                </Typography>
              );
            }

            if (index < activeStep) {
              stepProps.completed = true;
            }

            return (
              <Step key={label} {...stepProps}>
                <StepLabel {...labelProps}>{label}</StepLabel>
              </Step>
            );
          })}
        </Stepper>

        <Box sx={{ minHeight: 400 }}>
          {activeStep === 0 && (
            <CSVUploader
              onUpload={handleCsvUpload}
              onError={setError}
            />
          )}

          {activeStep === 1 && csvData && (
            <FieldMapper
              csvHeaders={csvData.headers}
              csvData={csvData.rows.slice(0, 5)} // Show first 5 rows for preview
              mappings={mappings}
              onMappingChange={handleMappingChange}
            />
          )}

          {activeStep === 2 && (
            <DuplicateResolver
              duplicates={duplicates}
              resolutions={duplicateResolutions}
              onResolutionChange={handleDuplicateResolution}
              csvData={csvData}
              mappings={mappings}
            />
          )}

          {activeStep === 3 && (
            <ImportProgress
              progress={importProgress}
              onCancel={cancelImport}
            />
          )}

          {activeStep === 4 && importResults && (
            <Box>
              {/* Main Success/Status Alert */}
              {!importResults.rolledBack && (
                <Alert
                  severity="success"
                  icon={<CheckIcon />}
                  sx={{ mb: 3 }}
                >
                  Import completed successfully!
                </Alert>
              )}

              {importResults.rolledBack && (
                <Alert
                  severity="info"
                  icon={<UndoIcon />}
                  sx={{ mb: 3 }}
                >
                  Import has been rolled back. All created entities have been permanently deleted.
                </Alert>
              )}

              {/* Rollback Status Display */}
              {showRollbackStatus && (
                <Alert severity="success" sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    ✅ Rollback Completed Successfully
                  </Typography>
                  <Typography variant="body2">
                    All {importResults.successful} entities created during this import have been permanently deleted.
                  </Typography>
                </Alert>
              )}

              {rollbackError && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setRollbackError(null)}>
                  <Typography variant="subtitle2" gutterBottom>
                    Rollback Error
                  </Typography>
                  <Typography variant="body2">
                    {rollbackError}
                  </Typography>
                </Alert>
              )}

              {/* Import Statistics */}
              <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                <Chip
                  label={`${importResults.successful} ${importResults.rolledBack ? 'Deleted' : 'Created'}`}
                  color={importResults.rolledBack ? 'default' : 'success'}
                  icon={importResults.rolledBack ? <UndoIcon /> : <CheckIcon />}
                />
                {importResults.canRollback && !importResults.rolledBack && (
                  <Chip
                    label="Rollback Available"
                    color="warning"
                    size="small"
                    icon={<WarningIcon />}
                  />
                )}
                {importResults.updated > 0 && (
                  <Chip
                    label={`${importResults.updated} Updated`}
                    color="info"
                  />
                )}
                {importResults.skipped > 0 && (
                  <Chip
                    label={`${importResults.skipped} Skipped`}
                    color="warning"
                    icon={<WarningIcon />}
                  />
                )}
                {importResults.failed > 0 && (
                  <Chip
                    label={`${importResults.failed} Failed`}
                    color="error"
                    icon={<ErrorIcon />}
                  />
                )}
              </Box>

              {/* Rollback Section */}
              {importResults.canRollback && !importResults.rolledBack && !rollbackProgress && (
                <Alert severity="warning" sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>
                        🗑️ Rollback Available
                      </Typography>
                      <Typography variant="body2">
                        You can permanently delete all {importResults.successful} entities created during this import.
                      </Typography>
                    </Box>
                    <Button
                      color="error"
                      variant="outlined"
                      onClick={() => setIsRollbackDialogOpen(true)}
                      startIcon={<UndoIcon />}
                      sx={{ ml: 2 }}
                    >
                      Rollback Import
                    </Button>
                  </Box>
                </Alert>
              )}

              {/* Rollback Progress Display */}
              {rollbackProgress && (
                <Alert severity="info" sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    🔄 Rolling Back Import...
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <CircularProgress size={20} />
                    <Typography variant="body2">
                      Deleted {rollbackProgress.deletedCount} of {rollbackProgress.totalToDelete} entities
                    </Typography>
                  </Box>
                  {rollbackProgress.failedDeletes && rollbackProgress.failedDeletes.length > 0 && (
                    <Typography variant="body2" color="error">
                      ⚠️ Failed to delete {rollbackProgress.failedDeletes.length} entities
                    </Typography>
                  )}
                </Alert>
              )}

              {/* Entity Preview Section */}
              {!importResults.rolledBack && importResults.successful > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Button
                    size="small"
                    onClick={() => setShowRollbackStatus(!showRollbackStatus)}
                    endIcon={showRollbackStatus ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    sx={{ mb: 2 }}
                  >
                    {showRollbackStatus ? 'Hide' : 'Show'} Created Entities Summary
                  </Button>

                  {showRollbackStatus && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      <Typography variant="body2">
                        This import created {importResults.successful} new person entities in the system.
                        Each entity has been assigned default permissions and is ready for use.
                      </Typography>
                    </Alert>
                  )}
                </Box>
              )}

              {/* Error Details */}
              {importResults.errors && importResults.errors.length > 0 && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Some rows had issues:
                  </Typography>
                  <Box component="ul" sx={{ m: 0, pl: 2 }}>
                    {importResults.errors.slice(0, 5).map((err, idx) => (
                      <li key={idx}>
                        Row {err.row}: {err.message}
                      </li>
                    ))}
                    {importResults.errors.length > 5 && (
                      <li>...and {importResults.errors.length - 5} more</li>
                    )}
                  </Box>
                </Alert>
              )}

              {/* Action Buttons */}
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  onClick={handleReset}
                  startIcon={<CloudUploadIcon />}
                  disabled={!!rollbackProgress}
                >
                  Import Another File
                </Button>
              </Box>
            </Box>
          )}
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 3 }}>
          <Button
            disabled={activeStep === 0 || isProcessing || !!rollbackProgress}
            onClick={handleBack}
            startIcon={<ArrowBackIcon />}
          >
            Back
          </Button>

          <Box>
            {activeStep === 3 && importProgress.status === 'processing' && (
              <Button
                color="error"
                onClick={cancelImport}
                startIcon={<CancelIcon />}
                sx={{ mr: 2 }}
              >
                Cancel Import
              </Button>
            )}

            {activeStep < 2 && (
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={!csvData || (activeStep === 1 && Object.keys(mappings).length === 0) || isProcessing || !!rollbackProgress}
                endIcon={isProcessing ? <CircularProgress size={20} /> : <ArrowForwardIcon />}
              >
                {isProcessing ? 'Processing...' : 'Next'}
              </Button>
            )}

            {activeStep === 2 && (
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={isProcessing || !!rollbackProgress}
                endIcon={isProcessing ? <CircularProgress size={20} /> : <CloudUploadIcon />}
              >
                {isProcessing ? 'Starting Import...' : 'Start Import'}
              </Button>
            )}
          </Box>
        </Box>
      </Paper>

      {/* Rollback Confirmation Dialog */}
      <RollbackConfirmationDialog
        open={isRollbackDialogOpen}
        onClose={() => setIsRollbackDialogOpen(false)}
        onConfirm={handleRollbackImport}
        importResults={importResults}
        isRollingBack={!!rollbackProgress}
        rollbackProgress={rollbackProgress}
      />
    </Box>
  );
}

export default CSVImportWizard;